import { memo as Memo, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';

//* HOC's
import withUIContext from '@context/consumerHOC/UIConsumer';
import withLanguageContext from '@/context/consumerHOC/LanguageConsumer';

//* Helpers
import isMobile from '@helpers/isMobile';
import stringToLetters from '@helpers/stringToLetters';

//* Components
import Text from '@components/global/Text';
import CustomLink from '@components/global/CustomLink';

//* Styles
import TableItemStyle from './style';

const TableItem = Memo(
	({ active, animation, title, description, minContent, minWidth, list, details, numberIndex, count, extensions, underline, url, as, animationDelay, visitWebsiteUrl, className, ...props }) => {
		//! Refs
		const ref = useRef(null);
		const topRef = useRef(null);
		const numbersArr = useRef([]);
		const isFirstMount = useRef(true);

		//! Timeline
		const tl = useMemo(() => gsap.timeline(), []);

		//! Main Animation
		useLayoutEffect(() => {
			if (!topRef.current?.children) return;

			const description = topRef.current.children[1];

			let arr = [topRef.current, topRef.current.children[0], description?.children?.[0], description?.children?.[0]?.children, description?.children?.[1], description?.children?.[1]?.children];
			let arr1 = description?.children?.[0]?.childNodes ? [description.children[0].childNodes] : [];

			arr = arr.filter((el) => el != null && el.length !== 0);
			arr1 = arr1.filter((el) => el != null && el.length !== 0);

			if (isFirstMount.current) {
				isFirstMount.current = false;

				if (!active) {
					gsap.set(arr, { opacity: 0 });
					return;
				}
			}

			tl.clear();

			if (active) {
				const delay = animationDelay !== undefined ? animationDelay : 1.2;

				tl.to(arr, { opacity: 1, stagger: 0.2, delay });

				if (details && arr1[0]?.length) {
					for (let i = 0; i < arr1[0].length; i++) {
						if (!numbersArr.current[i]) {
							numbersArr.current[i] = arr1[0][i].children[numberIndex || 0].innerHTML;
						}

						const val = numbersArr.current[i];
						const startVal = isNaN(Number(val)) ? val.replace(/[1-9]+/g, '0') : `0`.padStart(val.length, '0');

						gsap.fromTo(
							arr1[0][i].children[numberIndex || 0],
							{ textContent: startVal },
							{
								textContent: val,
								delay: 0.7 + delay,
								duration: 1,
								snap: { textContent: 1 },
								onUpdate: function () {
									arr1[0][i].children[numberIndex || 0].innerHTML = arr1[0][i].children[numberIndex || 0].innerHTML.padStart(val.length, '0');
								},
							}
						);
					}
				}
			} else {
				tl.to(arr, { opacity: 0, duration: 0, stagger: 0 });
			}
		}, [active, animationDelay, numberIndex, details, tl]);

		//! Letter Spacing Animation
		useEffect(() => {
			if (ref.current?.children) {
				gsap.set(ref.current.children[0].children[0].childNodes, {
					letterSpacing: 0,
				});
			}
		}, []);

		//! Portfolio Item Animation
		const portfolioItemAnimation = useCallback((enter) => {
			if (!isMobile()) {
				const arr = ref.current.children[0].children[0].childNodes;

				if (enter) {
					arr.forEach((el, i) => {
						gsap.to(el, {
							duration: 0.3,
							letterSpacing: (arr.length - i) / 2,
						});
					});
				} else {
					arr.forEach((el) => {
						gsap.to(el, { duration: 0.3, letterSpacing: 0 });
					});
				}
			}
		}, []);

		return (
			<TableItemStyle
				ref={topRef}
				className={`tableWrap ${count ? 'count' : ''} ${underline ? 'underline' : ''} ${className || ''}`}>
				<div className={`title ${props.titleStyle || ''}`}>
					<Text
						tag={'h2'}
						className='h6 font-anonymous font-regular'>
						{title}
					</Text>

					{animation && props.winWidth >= props.screenSizes.screenXS ? <div className={`animWrap animationWrap`}>{animation}</div> : null}
				</div>

				<div className={`description ${minContent ? 'minContent' : ''} ${minWidth ? 'minWidth' : ''}`}>
					{details && (
						<div className={'detailsWrap'}>
							{details.map((item, ind) => (
								<div
									key={ind}
									className={'detailsItem'}>
									{item.title ? <Text className='p-m font-anonymous font-semi-bold'>{item.title}</Text> : null}

									<Text
										tag={'h3'}
										className='p-l font-anonymous font-bold'>
										{item.number}
									</Text>

									<Text className='p-m font-poppins font-regular'>{item.description}</Text>
								</div>
							))}
						</div>
					)}

					{description && <Text className={`white-space h6 font-poppins font-light ${details?.length > 0 ? 'awards-text' : null}`}>{description}</Text>}

					{visitWebsiteUrl && (
						<CustomLink
							external
							url={visitWebsiteUrl}
							className={`crCircle link m-top`}>
							<Text
								className={`p-m font-poppins font-medium font-italic font-underline`}
								text={'visitTheWebsite'}
							/>
						</CustomLink>
					)}

					{list?.length ? (
						<ul className={'listWrap'}>
							{list.map((item, ind) => (
								<li key={ind}>
									<Text className='p-m font-poppins font-regular'>{item.title}</Text>
								</li>
							))}
						</ul>
					) : null}

					{(extensions || url) && (
						<div className={'extensionsWrap'}>
							{extensions &&
								extensions.slice(0, 5).map((item, ind) => (
									<Text
										key={ind}
										className={`extensions h6 font-poppins font-light`}>
										{item.title}
									</Text>
								))}

							{url && (
								<div
									ref={ref}
									className='link-m-top'
									onMouseEnter={() => portfolioItemAnimation(true)}
									onMouseLeave={() => portfolioItemAnimation(false)}>
									<CustomLink
										url={url}
										className={`link h6 font-poppins font-regular font-italic`}
										content={stringToLetters(props.translate('learn more'))}
									/>
								</div>
							)}
						</div>
					)}
				</div>

				{animation && props.winWidth < props.screenSizes.screenXS ? <div className={`animWrap animationWrap`}>{animation}</div> : null}
			</TableItemStyle>
		);
	}
);

export default withUIContext(withLanguageContext(TableItem, ['translate']), ['winWidth', 'screenSizes']);
